import React, { SetStateAction, useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock } from "@fortawesome/pro-regular-svg-icons";

import { StatusCode } from "api/protocols";
import { GetTeeTime as GetFacilityTeeTime } from "api/rpc/2022-09/facilityAdmin/teesheet/teeSheet";
import { GetTeeTime as GetClientTeeTime } from "api/rpc/2024-04/clientAdmin/teesheet/teeSheet";

import { ITeeTime } from "redux/reducers/models/teetime";

import { useAppSelector } from "hooks/redux";
import { convertTime, formatDate } from "helpers/Helpers";

import DatePickerInput from "components/datePickerInput/DatePickerInput";
import Popover from "components/popover/Popover";
import { Select } from "components/select/index";
import { useTranslation } from "react-i18next";
import Spin from "components/spin/spin";
import Toggle from "components/form/toggle/Toggle";

interface IMoveBookingTab {
  isActive: boolean;
  bookingStateQuantity: number;
  bookingStateHoles: number;
  course_id: number;
  facility_id?: number;
  confirmMoveBooking: (selectedTeeTimeId: number, resend: boolean) => void;
  closePopover: () => void;
  userLevel: "facility" | "client";
}

export default function MoveBookingTab(props: IMoveBookingTab) {
  const { Option } = Select;
  const { t } = useTranslation();

  const teeSheetStore = useAppSelector(store =>
    props.userLevel === "facility" ? store.teeSheetStore : store.clientTeeSheetStore,
  );

  const [resend, setResend] = useState(false);
  const [date, setDate] = useState<Date | null>(null);
  const [selectedTeeTimeId, setSelectedTeeTimeId] = useState<number | undefined>(undefined);
  const [loadingTeeTimes, setLoadingTeeTimes] = useState(false);
  const [allAvailableTeeTimes, setAllAvailableTeeTimes] = useState([]);

  const availableTimes = useMemo(() => {
    const returnData: Partial<ITeeTime>[] = [];

    allAvailableTeeTimes.forEach((teetime: ITeeTime) => {
      const eligible =
        props.bookingStateHoles === 18
          ? teetime.blocked_type === "open" &&
            teetime.divisions_available.includes(2) &&
            teetime.turn_tee_time !== null
          : teetime.blocked_type === "open";

      if (eligible) {
        returnData.push({
          id: teetime.id,
          start_time: convertTime(teetime.start_time),
          course_id: teetime.course_id,
          tournament_id: teetime.tournament_id,
          league_id: teetime.league_id,
        });
      }
    });

    return returnData;
  }, [allAvailableTeeTimes, props.bookingStateHoles]);

  // Load tee times when date changes
  useEffect(() => {
    if (date) {
      void fetchAvailableTeeTimes();
      setLoadingTeeTimes(false);
    }
  }, [date]);

  async function fetchAvailableTeeTimes() {
    const formattedDate = formatDate(date);

    const apiParams = {
      date: formattedDate,
      quantity_remaining: props.bookingStateQuantity,
      turn_tee_time: true,
      facility_id: props.facility_id,
    };

    const apiCall =
      props.userLevel === "facility"
        ? GetFacilityTeeTime(apiParams, false)
        : GetClientTeeTime(apiParams, false);

    const res = await apiCall;

    if (res.status === StatusCode.OK) {
      setAllAvailableTeeTimes(res.data as SetStateAction<any[]>);
      setLoadingTeeTimes(true);
    }
  }

  function handleConfirm() {
    if (!selectedTeeTimeId) {
      return;
    }
    props.confirmMoveBooking(selectedTeeTimeId, resend);
  }

  return (
    <div className="bm-move-booking-popover-container">
      <Popover active={props.isActive}>
        <div className="bm-move-booking-container">
          <p className="bm-move-booking-title">{t("Move booking")}</p>
          <hr />

          {/* DATE PICKER */}
          <DatePickerInput
            months={1}
            position="left"
            startingDate={date}
            disablePastDays
            setStartingDate={setDate}
          />

          {/* TIME SELECT */}
          {loadingTeeTimes ? (
            <div className="bm-move-booking-time-wrapper">
              <FontAwesomeIcon className="bm-clock-icon" icon={faClock} />

              <Select
                className="bm-move-booking-select time-select"
                defaultValue={"0"}
                onChange={(value: number) => setSelectedTeeTimeId(value)}
              >
                <Option value="0">{t("Select time")}</Option>
                {availableTimes
                  .filter(t => t.course_id === teeSheetStore.selectedTeeTime?.course_id)
                  .map(teetime => (
                    <Option key={teetime.id} value={teetime.id}>
                      {teetime.start_time}
                    </Option>
                  ))}
              </Select>
            </div>
          ) : (
            <div style={{ marginTop: "20px", height: "40px" }}>
              <Spin className="self-center" />
            </div>
          )}

          {/* TOGGLE */}
          <div className="pt-3 flex items-center gap-2">
            <Toggle size="small" checked={resend} onChange={() => setResend(v => !v)} labelRight={"Resend confirmation"} />
          </div>

          {/* BUTTONS */}
          <div className="bm-move-booking-actions">
            <button
              type="button"
              className="bm-move-booking-cancel"
              onClick={props.closePopover}
            >
              {t("Cancel")}
            </button>

            <button
              type="button"
              className="bm-move-booking-confirm"
              onClick={handleConfirm}
              disabled={!selectedTeeTimeId}
            >
              {t("Move")}
            </button>
          </div>
        </div>
      </Popover>
    </div>
  );
}
